import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Alert, AlertDescription } from './ui/alert'
import { Button } from './ui/button'
import { RefreshCw, AlertTriangle, Home } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  level?: 'page' | 'component'
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  retryCount: number
}

export class ErrorBoundary extends Component<Props, State> {
  private readonly maxRetries = 2

  constructor(props: Props) {
    super(props)
    this.state = { 
      hasError: false, 
      retryCount: 0 
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error, 
      retryCount: 0 
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo })
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo)
    }
    
    // TODO: In production, send to error monitoring service
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo)
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // TODO: Integrate with error monitoring service (e.g., Sentry)
    console.error('Production error logged:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      level: this.props.level || 'component'
    })
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1
    }))
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isPageLevel = this.props.level === 'page'
      const canRetry = this.state.retryCount < this.maxRetries
      const errorMessage = this.state.error?.message || 'An unexpected error occurred'

      return (
        <div className={`flex items-center justify-center p-6 ${isPageLevel ? 'min-h-screen' : 'min-h-[400px]'}`}>
          <div className={`w-full ${isPageLevel ? 'max-w-lg' : 'max-w-md'}`}>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="mt-2">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium mb-2">
                      {isPageLevel ? 'Page Error' : 'Something went wrong'}
                    </p>
                    <p className="text-sm">
                      {errorMessage}
                    </p>
                    {this.state.retryCount > 0 && (
                      <p className="text-xs mt-2 text-gray-600">
                        Retry attempt {this.state.retryCount} of {this.maxRetries}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    {canRetry && (
                      <Button 
                        onClick={this.handleRetry}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    )}
                    
                    {isPageLevel && (
                      <Button 
                        onClick={this.handleGoHome}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                      >
                        <Home className="h-4 w-4 mr-2" />
                        Go Home
                      </Button>
                    )}
                  </div>

                  {!canRetry && (
                    <div className="text-xs text-gray-600">
                      <p>Multiple retry attempts failed. Please refresh the page or contact support.</p>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
            
            {/* Development error details */}
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded text-sm">
                <summary className="cursor-pointer font-medium mb-2">
                  Error Details (Development Only)
                </summary>
                <div className="space-y-2">
                  <div>
                    <strong>Stack Trace:</strong>
                    <pre className="mt-1 overflow-auto whitespace-pre-wrap text-xs">
                      {this.state.error?.stack}
                    </pre>
                  </div>
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 overflow-auto whitespace-pre-wrap text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Higher-order component for wrapping components with error boundaries
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    fallback?: ReactNode
    level?: 'page' | 'component'
    onError?: (error: Error, errorInfo: ErrorInfo) => void
  }
) {
  const WrappedComponent = React.forwardRef<any, P>((props, ref) => {
    return (
      <ErrorBoundary 
        fallback={options?.fallback}
        level={options?.level}
        onError={options?.onError}
      >
        <Component {...props as P} />
      </ErrorBoundary>
    )
  })
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`
  
  return WrappedComponent
}

// Specialized error boundaries for different contexts
export function PageErrorBoundary({ children, onError }: { 
  children: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void 
}) {
  return (
    <ErrorBoundary level="page" onError={onError}>
      {children}
    </ErrorBoundary>
  )
}

export function ComponentErrorBoundary({ children, fallback, onError }: { 
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}) {
  return (
    <ErrorBoundary level="component" fallback={fallback} onError={onError}>
      {children}
    </ErrorBoundary>
  )
}

// Hook for programmatic error handling
export function useErrorHandler() {
  const throwError = React.useCallback((error: Error) => {
    throw error
  }, [])

  return { throwError }
}